<script setup lang="ts">
import { computed, ref } from 'vue'
import { NCheckbox, NInput, NSelect } from 'naive-ui'
import type { InputProps, SelectGroupOption } from 'naive-ui'
import AgentAmberButton from './AgentAmberButton.vue'
import type { AgentPrompt } from '../stores/agent_prompts'

// The /agent page's message form, split out of AgentView so that file
// stays under the 300-line cap — this owns the repo picker, the
// canned-prompt picker, the textarea, and the send/cancel buttons.
// `repo`/`draft` are v-models so the parent still owns the actual state
// (needed for `send()`'s API call and `newSession()`'s reset). Reuses the
// Prompt Library's `AgentPrompt` type rather than a component-local one —
// `<script setup>` can't expose named type exports to plain .ts importers
// (only other .vue SFCs), so a new local type would be unusable from this
// component's own spec file.
const props = defineProps<{
  repo: string
  draft: string
  repoOptions: Array<{ label: string; value: string }>
  prompts: AgentPrompt[]
  isNewSession: boolean
  canSend: boolean
  sending: boolean
  activeRunning: boolean
  agentRunning: boolean
  homeChannelName?: string | null
  postToChannel?: boolean
}>()

const emit = defineEmits<{
  'update:repo': [value: string]
  'update:draft': [value: string]
  'update:postToChannel': [value: boolean]
  send: []
  cancel: []
}>()

const generalPrompts = computed(() => props.prompts.filter((p) => p.repo_slug === null))
const repoPrompts = computed(() => props.prompts.filter((p) => p.repo_slug === props.repo))
const hasPrompts = computed(() => generalPrompts.value.length > 0 || repoPrompts.value.length > 0)

// Grouped dropdown — chips felt cluttered once a scope had more than a
// couple of prompts. Options key on prompt id, not label — labels aren't
// guaranteed unique.
const promptOptions = computed<SelectGroupOption[]>(() => {
  const groups: SelectGroupOption[] = []
  if (generalPrompts.value.length > 0) {
    groups.push({
      type: 'group',
      key: 'general',
      label: 'General',
      children: generalPrompts.value.map((p) => ({ label: p.label, value: p.id })),
    })
  }
  if (repoPrompts.value.length > 0) {
    groups.push({
      type: 'group',
      key: props.repo,
      label: props.repo,
      children: repoPrompts.value.map((p) => ({ label: p.label, value: p.id })),
    })
  }
  return groups
})

// Held locally rather than as a v-model into the parent: picking a prompt
// is a one-shot action (replace the draft), not persistent selection state,
// so the dropdown resets to its placeholder after every pick.
const selectedPromptId = ref<number | null>(null)

// Replaces the draft outright (no cursor-insertion) — the field stays a
// normal editable textarea afterward, per the parent PRD's decision.
function onPromptSelect(id: number) {
  selectedPromptId.value = null
  const prompt = props.prompts.find((p) => p.id === id)
  if (prompt) emit('update:draft', prompt.text)
}

// The textarea sits inside the composer's own outlined field, so Naive's
// fill and focus glow give way to it.
const FIELD_THEME: NonNullable<InputProps['themeOverrides']> = {
  color: 'transparent',
  colorFocus: 'transparent',
  boxShadowFocus: 'none',
}

function onEnter(event: KeyboardEvent) {
  // Don't submit mid-IME-composition (CJK/accented input) — Enter there is
  // committing a character, not sending the message.
  if (event.isComposing) return
  emit('send')
}
</script>

<template>
  <form
    class="flex flex-col gap-2 px-4 pt-3 pb-4 sm:px-7 sm:pb-6"
    data-testid="agent-form"
    @submit.prevent="emit('send')"
  >
    <div v-if="isNewSession" class="flex flex-wrap gap-2">
      <NSelect
        :value="repo"
        :options="repoOptions"
        placeholder="Repo (defaults to superbrain)"
        clearable
        class="min-w-[180px] flex-1"
        data-testid="agent-repo"
        @update:value="emit('update:repo', $event)"
      />
      <NSelect
        v-if="hasPrompts"
        :value="selectedPromptId"
        :options="promptOptions"
        placeholder="Load a canned prompt…"
        class="min-w-[180px] flex-1"
        data-testid="agent-prompt-picker"
        @update:value="onPromptSelect"
      />
    </div>
    <div
      class="flex min-w-0 items-end gap-2 rounded-[14px] border border-border-control bg-surface-raised py-2 pr-2 pl-1.5 focus-within:border-brand-primary"
    >
      <NInput
        :value="draft"
        class="min-w-0 flex-1"
        type="textarea"
        :bordered="false"
        :theme-overrides="FIELD_THEME"
        :autosize="{ minRows: 1, maxRows: 6 }"
        :placeholder="isNewSession ? 'Describe the task for a new session…' : 'Steer the agent…'"
        :input-props="{ 'aria-label': 'Message the agent' }"
        data-testid="agent-input"
        @update:value="emit('update:draft', $event)"
        @keydown.enter.exact.prevent="onEnter"
      />
      <button
        v-if="activeRunning"
        type="button"
        class="min-h-10 shrink-0 rounded-[10px] border border-status-alert/60 px-3 text-sm font-medium text-status-alert hover:bg-status-alert/10"
        data-testid="agent-cancel"
        @click="emit('cancel')"
      >
        Cancel run
      </button>
      <AgentAmberButton
        class="min-h-10 shrink-0"
        :disabled="!canSend"
        :loading="sending"
        data-testid="agent-send"
        @click="emit('send')"
      >
        Send
      </AgentAmberButton>
    </div>
    <NCheckbox
      v-if="homeChannelName && !isNewSession"
      :checked="postToChannel ?? false"
      data-testid="agent-post-to-channel"
      @update:checked="emit('update:postToChannel', $event)"
    >
      Also post to #{{ homeChannelName }}
    </NCheckbox>
    <p class="m-0 text-xs text-ink-faint" data-testid="agent-hint">
      <template v-if="agentRunning">Agent still working — your message will send once it's done.</template>
      <template v-else>Enter to send · Shift+Enter for a newline.</template>
    </p>
  </form>
</template>
